import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { HTML } from '@adornis/cms/db/scalars.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { ColorDefinitions } from './ColorDefinitions.js';
import { GlobalSetting } from './global-setting.js';
import { Setting } from './settings.js';
import type { User } from './user.js';

@Entity()
export class Institution extends MongoEntity {
  static override _class = 'Institution';
  static override _collectionName = 'institutions';

  @validate(nonOptional())
  @Field(type => String)
  name!: string;

  // hidden in admin-einrichtungs dashboard + user registration. Still visible in global admin area
  @Field(type => Boolean, { default: v => v ?? false })
  hidden!: boolean;

  @Field(type => ID)
  logoID?: string;

  @Field(type => ID)
  emailBannerImageID: Maybe<string>;

  @Field(type => Boolean, { default: v => v ?? false })
  maintenanceMode!: boolean;

  @Field(type => HTML)
  maintenanceModeRender: Maybe<string>;

  @Field(type => String)
  shortDescription: Maybe<string>;

  @Field(type => String)
  ccMail: Maybe<string>;

  @Field(type => Setting, {
    resolve: function (this: Institution) {
      return async () => {
        const result = (await loadMongoData(Setting, { institutionID: this._id })) as Setting[];
        return result[0] ?? new Setting({ institutionID: this._id });
      };
    },
  })
  settings!: Setting;

  @Field(type => Int, { default: v => v ?? 14 })
  maximumParallelSignups: Maybe<number>;

  @Field(type => Boolean, { default: v => v ?? false })
  isLDAPOnly: Maybe<boolean>;

  @Field(type => Boolean, { default: v => v ?? true })
  sortProductsByTime: Maybe<boolean>;

  @validate(nonOptional())
  @Field(type => ColorDefinitions, { default: v => v ?? new ColorDefinitions({}) })
  colorDefintions!: ColorDefinitions;

  // html string generated by html-based-buildify
  @Field(type => String)
  productPage?: string;

  @Field(type => String)
  productPageWorkshopCard?: string;

  @Field(type => String)
  productPageCounselingCard?: string;

  @Field(type => String)
  productPageWorkshopSetCard?: string;

  // OPTIONALLY OVERRIDE GLOBAL SETTINGS

  @Field(type => HTML) termsOfUse: Maybe<string>;
  @Field(type => HTML) imprint: Maybe<string>;
  @Field(type => HTML) privacy: Maybe<string>;
  @Field(type => HTML) accessibility: Maybe<string>;

  @Field(type => GlobalSetting, {
    resolve: function (this: Institution) {
      return async (gqlFields: BaseQLSelectionSet<GlobalSetting>) => GlobalSetting.getGlobalSetting()(gqlFields);
    },
  })
  globalSettings: Maybe<GlobalSetting>;

  @Field(type => [String])
  plugins: Maybe<string[]>;

  @Field(type => Boolean, { default: value => value ?? false })
  _feature_counselings!: boolean;

  @Field(type => Boolean, {
    resolve: function (this: Institution) {
      return async () => {
        // import inside resolve to not bundle config in client code
        const whatsappConfig = await import('@adornis/whatsapp/config.js').then(m => m.whatsappConfig);
        const id = whatsappConfig.get('appId');
        return !!id && id !== 'test' && id.length > 4;
      };
    },
  })
  whatsappEnabled!: boolean;

  // (aufsteigend sortiert)
  @Field(type => Int)
  sortingWeight?: number;

  get resolvedTermsOfUse() {
    return this.termsOfUse || this.globalSettings?.termsOfUse || '';
  }

  get resolvedImprint() {
    return this.imprint || this.globalSettings?.imprint || '';
  }

  get resolvedPrivacy() {
    return this.privacy || this.globalSettings?.dataPrivacy || '';
  }

  get resolvedAccessibility() {
    return this.accessibility || this.globalSettings?.accessibility || '';
  }

  static override get allFields() {
    return selectionSet(() => this, 3);
  }

  static async getAllowedInstitutions(adminAreaOnly: boolean = false) {
    // check permissions
    const userID = context.serverContext ? undefined : (context.userID as string | undefined);
    if (!userID) return [];
    const user = context.serverContext
      ? undefined
      : await CurrentUserInfo.getMyself<User>()({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!user) return [];
    const institutionCollection = await getRawCollection(Institution._collectionName);

    if (!adminAreaOnly || user.isSuperAdmin) {
      const result = await institutionCollection.aggregate<{ _id: string }>([{ $project: { _id: 1 } }]).toArray();
      return result.map(res => res._id);
    }

    const slotIDs: string[] = [];

    // ADMIN
    if (user.isAdmin) {
      slotIDs.push(...(user.adminRole?.contexts ?? []));
    }

    return slotIDs;
  }

  public static async _getInstitutionByID(institutionID: string): Promise<Maybe<Institution>> {
    const institutionCollection = await getRawCollection<Institution>(Institution._collectionName);
    const institution = await institutionCollection.findOne<Institution>({ _id: institutionID });
    return constructValue(institution);
  }
}
